import React, { useEffect, useMemo, useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { RefreshButton } from '../shared/RefreshButton';
import { GoogleConnectCard } from '../shared/GoogleConnectCard';
import { useGoogleAuth } from '../../hooks/useGoogleAuth';
import { googleCalendar, CalendarEvent } from '../../services/googleCalendar';
import { CalendarIcon, ClockIcon, VideoIcon, MapPinIcon, UsersIcon, ExternalLinkIcon, AlertCircleIcon, XIcon, CalendarDaysIcon, CheckCircle2Icon, ArrowRightIcon, MoreHorizontalIcon } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 10
  },
  show: {
    opacity: 1,
    y: 0
  }
};
// Helper to group events by day
function groupEventsByDay(events: CalendarEvent[]) {
  const groups: Record<string, CalendarEvent[]> = {};
  events.forEach(event => {
    const date = new Date(event.start.dateTime || event.start.date || '');
    const dateStr = date.toDateString();
    if (!groups[dateStr]) groups[dateStr] = [];
    groups[dateStr].push(event);
  });
  return Object.entries(groups).map(([dateStr, dayEvents]) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    let label = date.toLocaleDateString('es-AR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    });
    if (date.toDateString() === today.toDateString()) label = 'Hoy';else if (date.toDateString() === tomorrow.toDateString()) label = 'Mañana';
    return {
      date,
      label: label.charAt(0).toUpperCase() + label.slice(1),
      events: dayEvents.sort((a, b) => {
        const timeA = new Date(a.start.dateTime || a.start.date || '').getTime();
        const timeB = new Date(b.start.dateTime || b.start.date || '').getTime();
        return timeA - timeB;
      })
    };
  }).sort((a, b) => a.date.getTime() - b.date.getTime());
}
// Helper for attendee avatars
function getInitials(email?: string) {
  if (!email) return '??';
  return email.split('@')[0].substring(0, 2).toUpperCase();
}
export function GoogleCalendarPanel() {
  const {
    isConnected,
    isLoading: authLoading,
    signIn
  } = useGoogleAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissedError, setDismissedError] = useState(false);
  const [now, setNow] = useState(new Date());
  // Update current time every minute for "happening now" indicators
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(interval);
  }, []);
  const fetchEvents = async () => {
    if (!isConnected) return;
    setLoading(true);
    setError(null);
    setDismissedError(false);
    try {
      const data = await googleCalendar.getUpcoming(30);
      setEvents(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (isConnected) {
      fetchEvents();
    }
  }, [isConnected]);
  const groupedEvents = useMemo(() => groupEventsByDay(events), [events]);
  const stats = useMemo(() => {
    const todayStr = new Date().toDateString();
    const todayEvents = events.filter(e => new Date(e.start.dateTime || e.start.date || '').toDateString() === todayStr);
    // Calculate total hours today
    let totalMinutes = 0;
    todayEvents.forEach(e => {
      if (e.start.dateTime && e.end.dateTime) {
        const start = new Date(e.start.dateTime).getTime();
        const end = new Date(e.end.dateTime).getTime();
        totalMinutes += (end - start) / (1000 * 60);
      }
    });
    const hours = Math.floor(totalMinutes / 60);
    const mins = Math.round(totalMinutes % 60);
    const timeStr = hours > 0 ? `${hours}h ${mins > 0 ? `${mins}m` : ''}` : `${mins}m`;
    // Find next meeting
    const futureEvents = events.filter(e => {
      if (!e.start.dateTime) return false;
      return new Date(e.start.dateTime).getTime() > now.getTime();
    }).sort((a, b) => new Date(a.start.dateTime!).getTime() - new Date(b.start.dateTime!).getTime());
    const nextMeeting = futureEvents[0];
    let nextMeetingStr = 'Ninguna';
    if (nextMeeting?.start.dateTime) {
      const diff = new Date(nextMeeting.start.dateTime).getTime() - now.getTime();
      const diffMins = Math.round(diff / (1000 * 60));
      if (diffMins < 60) nextMeetingStr = `en ${diffMins} min`;else if (diffMins < 24 * 60) nextMeetingStr = `en ${Math.floor(diffMins / 60)} hs`;else nextMeetingStr = 'Mañana';
    }
    return {
      todayCount: todayEvents.length,
      timeInMeetings: timeStr || '0h',
      nextMeeting: nextMeetingStr,
      totalUpcoming: events.length
    };
  }, [events, now]);
  if (authLoading) {
    return <div className="h-full bg-[#020617] flex items-center justify-center" data-id="element-4618">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" data-id="element-4619" />
      </div>;
  }
  if (!isConnected) {
    return <GoogleConnectCard title="Google Calendar" description="Conectá tu cuenta de Google para ver tus próximas reuniones, detectar conflictos con tus sprints y organizar tu tiempo directamente desde el dashboard." accentColor="blue" highlightService="calendar" signIn={signIn} error={error} isLoading={authLoading} data-id="element-4620" />;
  }
  return <div className="h-full overflow-y-auto p-4 lg:p-8 bg-[#020617] relative" data-id="element-4621">
      <div className="absolute top-0 left-1/4 w-1/2 h-96 bg-blue-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-4622" />

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-4623">
        {/* Header */}
        <div className="relative pb-6 mb-8" data-id="element-4624">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-4625">
            <div data-id="element-4626">
              <div className="flex items-center gap-3 mb-2" data-id="element-4627">
                <div className="relative w-12 h-12 bg-slate-900 border border-slate-700 rounded-xl flex items-center justify-center shadow-lg shadow-blue-500/20" data-id="element-4628">
                  <CalendarIcon size={24} className="text-blue-400 relative z-10" data-id="element-4629" />

                  <div className="absolute inset-0 bg-blue-500/10 rounded-xl blur-sm" data-id="element-4630" />
                </div>
                <div data-id="element-4631">
                  <h1 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-slate-100 to-slate-400" data-id="element-4632">
                    Agenda
                  </h1>
                  <p className="text-sm text-slate-400 mt-1" data-id="element-4633">
                    Tus próximos eventos y disponibilidad
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4" data-id="element-4634">
              <RefreshButton onRefresh={fetchEvents} isRefreshing={loading} data-id="element-4635" />
            </div>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-blue-500/50 to-transparent" data-id="element-4636" />
        </div>

        {/* Error Banner */}
        <AnimatePresence data-id="element-4637">
          {error && !dismissedError && <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} className="mb-8 p-4 bg-red-950/40 border border-red-900/50 rounded-2xl flex items-start gap-3 text-red-400 backdrop-blur-md shadow-lg" data-id="element-4638">
              <AlertCircleIcon size={20} className="shrink-0 mt-0.5" data-id="element-4639" />
              <div className="flex-1" data-id="element-4640">
                <h4 className="text-sm font-bold mb-1 text-red-300" data-id="element-4641">
                  Error de conexión
                </h4>
                <p className="text-sm opacity-90" data-id="element-4642">{error}</p>
              </div>
              <button onClick={() => setDismissedError(true)} className="p-1.5 hover:bg-red-900/50 rounded-lg transition-colors" data-id="element-4643">
                <XIcon size={16} data-id="element-4644" />
              </button>
            </motion.div>}
        </AnimatePresence>

        {/* Rich Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-id="element-4645">
          {[{
          label: 'Reuniones Hoy',
          value: stats.todayCount,
          icon: CalendarDaysIcon,
          color: 'text-blue-400',
          bg: 'bg-blue-500/10',
          border: 'border-blue-500/20'
        }, {
          label: 'Tiempo en calls',
          value: stats.timeInMeetings,
          icon: ClockIcon,
          color: 'text-purple-400',
          bg: 'bg-purple-500/10',
          border: 'border-purple-500/20'
        }, {
          label: 'Próxima reunión',
          value: stats.nextMeeting,
          icon: ArrowRightIcon,
          color: 'text-emerald-400',
          bg: 'bg-emerald-500/10',
          border: 'border-emerald-500/20'
        }, {
          label: 'Próximos 7 días',
          value: stats.totalUpcoming,
          icon: UsersIcon,
          color: 'text-slate-300',
          bg: 'bg-slate-800/50',
          border: 'border-slate-700'
        }].map((stat, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.1
        }} className={`p-5 rounded-2xl border ${stat.border} ${stat.bg} backdrop-blur-sm relative overflow-hidden group`} data-id="element-4646">
              <div className={`absolute -right-4 -top-4 w-24 h-24 rounded-full ${stat.bg} blur-2xl group-hover:bg-opacity-20 transition-all`} data-id="element-4647" />

              <div className="relative z-10" data-id="element-4648">
                <div className="flex items-center gap-2 mb-3" data-id="element-4649">
                  <stat.icon size={16} className={stat.color} data-id="element-4650" />
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-4651">
                    {stat.label}
                  </span>
                </div>
                <span className={`text-3xl font-bold ${stat.color}`} data-id="element-4652">
                  {stat.value}
                </span>
              </div>
            </motion.div>)}
        </div>

        {/* Events Timeline */}
        {loading && events.length === 0 ? <div className="space-y-8" data-id="element-4653">
            {[1, 2].map(group => <div key={group} className="space-y-4" data-id="element-4654">
                <div className="h-6 w-32 bg-slate-800/50 rounded animate-pulse" data-id="element-4655" />
                {[1, 2, 3].map(i => <div key={i} className="bg-slate-900/40 border border-slate-800/50 rounded-2xl p-5 relative overflow-hidden h-28" data-id="element-4656">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-slate-800/20 to-transparent" data-id="element-4657" />
                  </div>)}
              </div>)}
          </div> : events.length === 0 ? <div className="flex flex-col items-center justify-center py-32 text-center border border-dashed border-slate-800/60 rounded-3xl bg-slate-900/20 backdrop-blur-sm" data-id="element-4658">
            <div className="w-24 h-24 bg-slate-900 rounded-full flex items-center justify-center mb-6 border border-slate-800 shadow-xl relative" data-id="element-4659">
              <div className="absolute inset-0 bg-blue-500/10 rounded-full blur-md" data-id="element-4660" />
              <CalendarIcon size={40} className="text-slate-600 relative z-10" data-id="element-4661" />
            </div>
            <h3 className="text-xl font-bold text-slate-200 mb-2" data-id="element-4662">
              Agenda libre
            </h3>
            <p className="text-slate-400 text-sm max-w-sm" data-id="element-4663">
              No tenés eventos programados para los próximos días. ¡Aprovechá
              para hacer focus work!
            </p>
          </div> : <div className="space-y-10" data-id="element-4664">
            {groupedEvents.map((group, groupIdx) => <motion.div key={group.label} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: groupIdx * 0.1
        }} data-id="element-4665">
                <h3 className="text-lg font-bold text-slate-200 mb-4 flex items-center gap-3 sticky top-0 bg-[#020617]/90 backdrop-blur-md py-2 z-20" data-id="element-4666">
                  {group.label}
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-800 to-transparent" data-id="element-4667" />
                </h3>

                <div className="space-y-3 relative before:absolute before:inset-0 before:ml-[4.5rem] before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-slate-800 before:to-transparent" data-id="element-4668">
                  {group.events.map((event, idx) => {
              const start = new Date(event.start.dateTime || event.start.date || '');
              const end = new Date(event.end.dateTime || event.end.date || '');
              const isAllDay = !event.start.dateTime;
              const isHappeningNow = !isAllDay && start.getTime() <= now.getTime() && end.getTime() >= now.getTime();
              const isPast = !isAllDay && end.getTime() < now.getTime();
              const meetLink = event.hangoutLink || event.conferenceData?.entryPoints?.find(e => e.entryPointType === 'video')?.uri;
              // Duration calculation for visual bar
              let durationMins = 60;
              if (!isAllDay) {
                durationMins = (end.getTime() - start.getTime()) / (1000 * 60);
              }
              const heightClass = durationMins <= 30 ? 'h-16' : durationMins <= 60 ? 'h-24' : 'h-32';
              return <motion.div variants={itemVariants} initial="hidden" animate="show" key={event.id} className={`relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active ${isPast ? 'opacity-60' : ''}`} data-id="element-4669">
                        {/* Timeline dot */}
                        <div className={`absolute left-[4.5rem] md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full border-2 border-[#020617] z-10 ${isHappeningNow ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.8)]' : isPast ? 'bg-slate-700' : 'bg-slate-500 group-hover:bg-blue-400 transition-colors'}`} data-id="element-4670">
                          {isHappeningNow && <div className="absolute inset-0 rounded-full bg-blue-500 animate-ping opacity-75" data-id="element-4671" />}
                        </div>

                        {/* Time (Left side on desktop, left side on mobile) */}
                        <div className="w-[4.5rem] md:w-[calc(50%-2rem)] flex flex-col items-start md:items-end pr-4 md:pr-8 shrink-0" data-id="element-4672">
                          <span className={`text-sm font-bold font-mono ${isHappeningNow ? 'text-blue-400' : 'text-slate-300'}`} data-id="element-4673">
                            {isAllDay ? 'Todo el día' : start.toLocaleTimeString('es-AR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                          </span>
                          {!isAllDay && <span className="text-xs text-slate-500 font-mono mt-0.5" data-id="element-4674">
                              {end.toLocaleTimeString('es-AR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                            </span>}
                        </div>

                        {/* Event Card (Right side on desktop, right side on mobile) */}
                        <div className="w-[calc(100%-5.5rem)] md:w-[calc(50%-2rem)] pl-4 md:pl-8 py-2" data-id="element-4675">
                          <div className={`bg-slate-900/60 backdrop-blur-md border rounded-2xl p-4 md:p-5 transition-all duration-300 relative overflow-hidden ${isHappeningNow ? 'border-blue-500/50 shadow-lg shadow-blue-900/20 ring-1 ring-blue-500/20' : 'border-slate-800/60 hover:border-slate-700 hover:shadow-md'}`} data-id="element-4676">
                            {/* Visual duration accent */}
                            <div className={`absolute left-0 top-0 bottom-0 w-1 ${isHappeningNow ? 'bg-blue-500' : isPast ? 'bg-slate-700' : 'bg-slate-600'}`} data-id="element-4677" />

                            <div className="flex flex-col gap-3" data-id="element-4678">
                              <div className="flex items-start justify-between gap-4" data-id="element-4679">
                                <h3 className={`text-base font-bold leading-tight ${isPast ? 'text-slate-400' : 'text-slate-100'}`} data-id="element-4680">
                                  {event.summary || '(Sin título)'}
                                </h3>
                                {meetLink && !isPast && <a href={meetLink} target="_blank" rel="noopener noreferrer" className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${isHappeningNow ? 'bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/25' : 'bg-slate-800 hover:bg-slate-700 text-blue-400 border border-slate-700'}`} data-id="element-4681">
                                    <VideoIcon size={14} data-id="element-4682" />{' '}
                                    <span className="hidden sm:inline" data-id="element-4683">
                                      Unirse
                                    </span>
                                  </a>}
                              </div>

                              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-400" data-id="element-4684">
                                {event.location && <span className="flex items-center gap-1.5 truncate max-w-[200px] bg-slate-950/50 px-2 py-1 rounded-md border border-slate-800/50" data-id="element-4685">
                                    <MapPinIcon size={12} className="text-slate-500" data-id="element-4686" />{' '}
                                    {event.location}
                                  </span>}

                                {event.attendees && event.attendees.length > 0 && <div className="flex items-center gap-2" data-id="element-4687">
                                      <div className="flex -space-x-2" data-id="element-4688">
                                        {event.attendees.slice(0, 3).map((att, i) => <div key={i} className="w-6 h-6 rounded-full bg-slate-800 border-2 border-slate-900 flex items-center justify-center text-[9px] font-bold text-slate-300" title={att.email} data-id="element-4689">
                                              {getInitials(att.email)}
                                            </div>)}
                                        {event.attendees.length > 3 && <div className="w-6 h-6 rounded-full bg-slate-800 border-2 border-slate-900 flex items-center justify-center text-[9px] font-bold text-slate-400" data-id="element-4690">
                                            +{event.attendees.length - 3}
                                          </div>}
                                      </div>
                                    </div>}
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>;
            })}
                </div>
              </motion.div>)}
          </div>}
      </div>
    </div>;
}